/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  AsYouType,
  parsePhoneNumberFromString
} from 'libphonenumber-js';
import {
  getSortedCountries,
  flagEmoji,
  countryLabel,
  callingCode,
  detectCountry,
  toE164,
  formatNational
} from '../../utils/phoneUtils';
import './PhoneInput.css';

/**
 * Country selector + national number field. Value is stored as E.164
 * (e.g. +441582297550) so the rest of the app does not care about formatting.
 */
const PhoneInput = ({
  value = '',
  onChange,
  disabled = false,
  required = false,
  id,
  name,
  placeholder,
  className = '',
  defaultCountry = 'US'
}) => {
  const countries = useMemo(() => getSortedCountries(), []);
  const [country, setCountry] = useState(() => detectCountry(value, defaultCountry));
  const [national, setNational] = useState(() => formatNational(value, detectCountry(value, defaultCountry)));

  useEffect(() => {
    if (!value) {
      setNational('');
      return;
    }
    const nextCountry = detectCountry(value, country || defaultCountry);
    if (nextCountry !== country) setCountry(nextCountry);
    const formatted = formatNational(value, nextCountry);
    if (formatted !== national) setNational(formatted);
    // Only re-sync when the incoming stored value changes (e.g. profile load).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (iso, nationalValue) => {
    if (!onChange) return;
    const digits = String(nationalValue || '').replace(/\D/g, '');
    if (!digits) {
      onChange('');
      return;
    }
    try {
      const formatter = new AsYouType(iso);
      formatter.input(digits);
      const parsed = formatter.getNumber() || parsePhoneNumberFromString(digits, iso);
      onChange(parsed ? parsed.number : toE164(digits, iso) || digits);
    } catch {
      onChange(toE164(digits, iso) || digits);
    }
  };

  const handleCountryChange = (e) => {
    const next = e.target.value;
    setCountry(next);
    const formatted = formatNational(national.replace(/\D/g, ''), next);
    setNational(formatted);
    emit(next, formatted);
  };

  const handleNumberChange = (e) => {
    const raw = e.target.value;
    try {
      const formatter = new AsYouType(country);
      const formatted = formatter.input(raw);
      setNational(formatted);
      emit(country, formatted);
    } catch {
      setNational(raw);
      emit(country, raw);
    }
  };

  return (
    <div className={`phone-input ${className}`.trim()}>
      <select
        className="phone-input-country"
        value={country}
        onChange={handleCountryChange}
        disabled={disabled}
        aria-label="Country"
        title={countryLabel(country)}
      >
        {countries.map(iso => (
          <option key={iso} value={iso}>
            {flagEmoji(iso)} {countryLabel(iso)} ({callingCode(iso)})
          </option>
        ))}
      </select>
      <input
        id={id}
        name={name}
        type="tel"
        className="phone-input-number"
        value={national}
        onChange={handleNumberChange}
        disabled={disabled}
        required={required}
        placeholder={placeholder || `${callingCode(country)} number`}
        autoComplete="tel-national"
      />
    </div>
  );
};

export default PhoneInput;
